/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import { useCallback, useMemo } from 'react';

// eslint-disable-next-line no-restricted-imports
import type { GridSize } from '@material-ui/core';
import type { Maybe } from '__generated__/graphql-types';

import { isRightToLeft } from 'js/lib/language';

import { Grid, IconButton, Typography2, breakpoints } from '@coursera/cds-core';

import Carousel from 'bundles/design-system/components/Carousel';
import type { CarouselSettings as CarouselSettingsType } from 'bundles/design-system/components/types';
import TrackedDiv from 'bundles/page/components/TrackedDiv';
import type { ImageType } from 'bundles/unified-common/components/Image';
import type { VideoItem } from 'bundles/unified-common/components/Video';
import { TESTIMONIAL_DISPLAY_STYLES } from 'bundles/unified-common/constants/testimonials';
import useIsMobile from 'bundles/unified-common/hooks/useIsMobile';
import useIsTablet from 'bundles/unified-common/hooks/useIsTablet';
import type { ModuleLayoutConfig } from 'bundles/unified-common/modules/ModuleLayout';
import ModuleLayout, { TEXT_TOP_IMAGE_BOTTOM } from 'bundles/unified-common/modules/ModuleLayout';
import { IMAGE_HEIGHT } from 'bundles/unified-common/modules/testimonials/CustomTestimonialItem';
import type { EventingType } from 'bundles/unified-description-page-common/contexts/EventingContext';

import _t from 'i18n!nls/unified-common';

export type Testimonial = {
  id?: Maybe<string>;
  authorName?: Maybe<string>;
  authorTitle?: Maybe<string>;
  quote?: Maybe<string>;
  image?: Maybe<ImageType>;
  video?: Maybe<VideoItem>;
  associatedProduct?: Maybe<string>;
  authorOrganization?: Maybe<string>;
  productName?: Maybe<string>;
  learnerSince?: Maybe<number>;
  eventingData?: Maybe<EventingType>;
};

export type TestimonialCarouselProps = {
  testimonialItems: React.ReactNode[];
  numTestimonialsToDisplay?: 1 | 2 | 3 | 4;
  headline?: Maybe<string>;
  displayStyle?: Maybe<string>;
} & ModuleLayoutConfig;

const styles = {
  carousel: (isCarouselMode: boolean) => css`
    padding: var(--cds-spacing-400) !important;

    // This chain of selectors is to make sure the testimonials inside
    // the carousel component have the same height.
    &,
    & > div,
    .rc-Carousel,
    .slick-slider,
    .slick-list,
    .slick-track,
    .slick-slide,
    .slick-slide > div {
      height: 100%;
    }

    .slick-prev {
      left: calc(var(--cds-spacing-200) * -1);
    }

    .slick-next {
      right: calc(var(--cds-spacing-200) * -1);
    }

    [dir='rtl'] {
      .slick-prev {
        right: calc(var(--cds-spacing-200) * -1);
      }

      .slick-next {
        left: calc(var(--cds-spacing-200) * -1);
      }
    }

    .slick-arrow {
      position: absolute;
      top: 40%;
      height: auto;
      z-index: 1;

      ${breakpoints.down('xs')} {
        top: ${isCarouselMode ? `calc(${IMAGE_HEIGHT}px + var(--cds-spacing-300))` : '40%'} !important;
      }
    }

    .slick-dots {
      gap: var(--cds-spacing-200);
      margin-top: var(--cds-spacing-200);

      button {
        height: 14px;
        width: 14px;
        background-color: var(--cds-color-grey-50);
        border-radius: var(--cds-border-radius-200);

        ::before {
          content: '' !important;
          width: 32px;
          height: 32px;
        }
      }

      .slick-active {
        button {
          background-color: var(--cds-color-grey-300);
        }
      }
    }
  `,
  headline: css`
    margin-bottom: var(--cds-spacing-300);

    ${breakpoints.down('md')} {
      margin-bottom: var(--cds-spacing-200);
    }
  `,
  iconButtonWrapper: css`
    display: inline-flex;
  `,
  iconButton: css`
    border-radius: 100%;
    display: inline-flex;
    vertical-align: middle;
  `,
  carouselWrapper: css`
    padding: 0 !important;
  `,
  testimonial: css`
    height: 100%;
  `,
};

const DEFAULT_NUM_PER_SLIDE = 4;

const TestimonialsModule = (props: TestimonialCarouselProps) => {
  const { testimonialItems, headline, displayStyle, numTestimonialsToDisplay = DEFAULT_NUM_PER_SLIDE } = props;
  const isTablet = useIsTablet();
  const isMobile = useIsMobile();
  const isCarouselMode = !!displayStyle && displayStyle === TESTIMONIAL_DISPLAY_STYLES.CAROUSEL;
  const slidesToShow = isCarouselMode ? 1 : 2;
  const testimonialSingularOrPlural = slidesToShow === 1 ? _t('testimonial') : _t('testimonials');
  const isRTL = isRightToLeft(_t.getLocale());
  const prevArrowLabel = _t('Go to previous #{testimonialSingularOrPlural}', {
    testimonialSingularOrPlural,
  });
  const nextArrowLabel = _t('Go to next #{testimonialSingularOrPlural}', {
    testimonialSingularOrPlural,
  });

  const carouselSettings: CarouselSettingsType = useMemo(
    () => ({
      slidesToShow,
      slidesToScroll: slidesToShow,
      responsive: [
        {
          breakpoint: breakpoints.values.sm,
          settings: {
            slidesToShow: 1,
            slidesToScroll: 1,
            variableWidth: false,
            dots: true,
          },
        },
        {
          breakpoint: breakpoints.values.md,
          settings: {
            slidesToShow,
            slidesToScroll: slidesToShow,
            variableWidth: false,
            dots: true,
          },
        },
      ],
      isFluid: true,
      fade: false,
      dots: true,
    }),
    [slidesToShow]
  );

  const contentPanel = useCallback(
    () => (
      <TrackedDiv
        trackingName="user_testimonial"
        withVisibilityTracking={true}
        requireFullyVisible={false}
        trackClicks={false}
      >
        {headline && (
          <Typography2 variant="titleSmall" component="h2" css={styles.headline} color="inherit">
            {headline}
          </Typography2>
        )}
        <Grid container spacing={32}>
          {!isTablet && !isMobile && slidesToShow > 1 ? (
            testimonialItems?.slice(0, numTestimonialsToDisplay).map((testimonial, index) => (
              // the passed grid value will always be an integer 4 or lower, and thus always a valid grid size
              // eslint-disable-next-line react/no-array-index-key
              <Grid item md={(12 / numTestimonialsToDisplay) as GridSize} key={index}>
                {testimonial}
              </Grid>
            ))
          ) : (
            <Grid item xs={12} css={styles.carouselWrapper}>
              <Grid item xs={12} css={styles.carousel(isCarouselMode)}>
                <Carousel
                  settings={{
                    ...carouselSettings,
                    prevArrow: (
                      <div aria-label={isRTL ? nextArrowLabel : prevArrowLabel} css={styles.iconButtonWrapper}>
                        <IconButton intent={isRTL ? 'next' : 'previous'} variant="secondary" css={styles.iconButton} />
                      </div>
                    ),
                    nextArrow: (
                      <div aria-label={isRTL ? prevArrowLabel : nextArrowLabel} css={styles.iconButtonWrapper}>
                        <IconButton intent={isRTL ? 'previous' : 'next'} variant="secondary" css={styles.iconButton} />
                      </div>
                    ),
                  }}
                >
                  {testimonialItems?.slice(0, numTestimonialsToDisplay).map((testimonial, index) => (
                    // eslint-disable-next-line react/no-array-index-key
                    <div key={index} css={styles.testimonial}>
                      {testimonial}
                    </div>
                  ))}
                </Carousel>
              </Grid>
            </Grid>
          )}
        </Grid>
      </TrackedDiv>
    ),
    [
      headline,
      isTablet,
      isMobile,
      slidesToShow,
      testimonialItems,
      isCarouselMode,
      carouselSettings,
      isRTL,
      nextArrowLabel,
      prevArrowLabel,
      numTestimonialsToDisplay,
    ]
  );

  return <ModuleLayout assembly={{ ...props, layout: TEXT_TOP_IMAGE_BOTTOM }} renderContent={contentPanel} />;
};

export default TestimonialsModule;
